// Menu Studio document operations, shared verbatim by the Studio UI, the API
// and the CLI (frontend copy: MenuStudio/core, kept identical by
// scripts/syncStudioCore.js). Every operation addresses pages and nodes by ID,
// never by array index, so a batch means the same thing after unrelated edits.
//
// Operation shapes (JSON-safe):
//   { op: "set", path: ["doc"|"page"|"node", id?, ...keys], value, unset?, expect? }
//   { op: "insertNode", pageId, index, node }
//   { op: "removeNode", pageId, id, expect? }
//   { op: "orderNodes", pageId, ids, expect? }
//   { op: "insertPage", index, page }
//   { op: "removePage", id, expect? }
//   { op: "orderPages", ids, expect? }
// `expect` is a precondition: { value } or { absent: true }. A failed
// precondition rejects the whole batch; nothing is partially applied.

const PAGE_KEYS_SKIPPED = new Set(["elements"]);
// Top-level keys whose members are diffed individually, so one style or group
// edit does not resend its siblings.
const KEYED_DOC_KEYS = new Set(["styles", "styleGroups", "cmsDescriptions", "assets", "textStyles", "blocks", "outputs"]);

class OpConflict extends Error {
  constructor(message, detail = {}) {
    super(message);
    this.name = "OpConflict";
    this.code = "conflict";
    this.detail = detail;
  }
}

const isObject = (v) => v !== null && typeof v === "object" && !Array.isArray(v);
const clone = (v) => (v === undefined ? undefined : JSON.parse(JSON.stringify(v)));

function deepEqual(a, b) {
  if (a === b) return true;
  if (typeof a === "number" && typeof b === "number") return Number.isNaN(a) && Number.isNaN(b);
  if (Array.isArray(a)) {
    if (!Array.isArray(b) || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (!deepEqual(a[i], b[i])) return false;
    return true;
  }
  if (isObject(a) && isObject(b)) {
    const ka = Object.keys(a).filter((k) => a[k] !== undefined);
    const kb = Object.keys(b).filter((k) => b[k] !== undefined);
    if (ka.length !== kb.length) return false;
    for (const k of ka) if (!deepEqual(a[k], b[k])) return false;
    return true;
  }
  return false;
}

const expectOf = (value) => (value === undefined ? { absent: true } : { value: clone(value) });
function meets(expect, current) {
  if (!expect) return true;
  if (expect.absent) return current === undefined;
  return deepEqual(expect.value, current);
}

function getIn(target, keys) {
  let value = target;
  for (const key of keys) {
    if (value === null || typeof value !== "object") return undefined;
    value = value[key];
  }
  return value;
}
// Copy-on-write: returns a new object with `keys` set (or removed), sharing
// every untouched branch with the original.
function setIn(target, keys, value, unset) {
  if (!keys.length) return unset ? undefined : value;
  const [key, ...rest] = keys;
  const base = isObject(target) || Array.isArray(target) ? target : {};
  const copy = Array.isArray(base) ? [...base] : { ...base };
  const next = setIn(base[key], rest, value, unset);
  if (next === undefined && (unset || !rest.length)) {
    if (Array.isArray(copy)) copy[key] = undefined;
    else delete copy[key];
  } else copy[key] = next;
  return copy;
}

// A mutable working copy that clones pages/elements lazily, once per batch.
function workspace(doc) {
  const state = { doc: { ...doc }, pages: [...doc.pages], pagesCopied: new Set(), index: null };
  state.reindex = () => {
    state.index = new Map();
    state.pageIndex = new Map();
    state.pages.forEach((page, p) => {
      state.pageIndex.set(page.id, p);
      page.elements.forEach((node, e) => state.index.set(node.id, { p, e }));
    });
  };
  state.reindex();
  state.page = (pageId, write) => {
    const p = state.pageIndex.get(pageId);
    if (p === undefined) return null;
    if (write && !state.pagesCopied.has(pageId)) {
      state.pages[p] = { ...state.pages[p], elements: [...state.pages[p].elements] };
      state.pagesCopied.add(pageId);
    }
    return state.pages[p];
  };
  state.finish = () => ({ ...state.doc, pages: state.pages });
  return state;
}

function describe(op) {
  if (op.op === "set") return `${op.op} ${op.path.join(".")}`;
  return `${op.op} ${op.id || op.pageId || ""}`.trim();
}

function applyOne(ws, op) {
  switch (op.op) {
    case "set": {
      const [scope, ...rest] = op.path || [];
      if (scope === "doc") {
        if (!rest.length || rest[0] === "pages") throw new OpConflict("Pages change through page operations.", { op: describe(op) });
        const current = getIn(ws.doc, rest);
        if (!meets(op.expect, current)) throw new OpConflict(`Expected value changed at ${rest.join(".")}.`, { op: describe(op) });
        ws.doc = setIn(ws.doc, rest, clone(op.value), op.unset);
        return { op: "set", path: op.path, ...(current === undefined ? { unset: true } : { value: clone(current) }), expect: expectOf(op.unset ? undefined : op.value) };
      }
      if (scope === "page") {
        const [pageId, ...keys] = rest;
        if (!keys.length || PAGE_KEYS_SKIPPED.has(keys[0]) || keys[0] === "id") throw new OpConflict("Invalid page path.", { op: describe(op) });
        const page = ws.page(pageId, true);
        if (!page) throw new OpConflict(`Page ${pageId} no longer exists.`, { op: describe(op) });
        const current = getIn(page, keys);
        if (!meets(op.expect, current)) throw new OpConflict(`Page ${pageId} changed.`, { op: describe(op) });
        ws.pages[ws.pageIndex.get(pageId)] = setIn(page, keys, clone(op.value), op.unset);
        return { op: "set", path: op.path, ...(current === undefined ? { unset: true } : { value: clone(current) }), expect: expectOf(op.unset ? undefined : op.value) };
      }
      if (scope === "node") {
        const [nodeId, ...keys] = rest;
        if (!keys.length || keys[0] === "id") throw new OpConflict("Invalid node path.", { op: describe(op) });
        const at = ws.index.get(nodeId);
        if (!at) throw new OpConflict(`Element ${nodeId} no longer exists.`, { op: describe(op), nodeId });
        const page = ws.page(ws.pages[at.p].id, true);
        const node = page.elements[at.e];
        const current = getIn(node, keys);
        if (!meets(op.expect, current)) throw new OpConflict(`Element ${nodeId} changed.`, { op: describe(op), nodeId });
        page.elements[at.e] = setIn(node, keys, clone(op.value), op.unset);
        return { op: "set", path: op.path, ...(current === undefined ? { unset: true } : { value: clone(current) }), expect: expectOf(op.unset ? undefined : op.value) };
      }
      throw new OpConflict("Unknown path scope.", { op: describe(op) });
    }
    case "insertNode": {
      if (!isObject(op.node) || typeof op.node.id !== "string") throw new OpConflict("insertNode needs a node with an id.");
      if (ws.index.has(op.node.id)) throw new OpConflict(`Element ${op.node.id} already exists.`, { nodeId: op.node.id });
      const page = ws.page(op.pageId, true);
      if (!page) throw new OpConflict(`Page ${op.pageId} no longer exists.`);
      const index = Number.isInteger(op.index) ? Math.max(0, Math.min(op.index, page.elements.length)) : page.elements.length;
      page.elements.splice(index, 0, clone(op.node));
      ws.reindex();
      return { op: "removeNode", pageId: op.pageId, id: op.node.id, expect: { value: clone(op.node) } };
    }
    case "removeNode": {
      const at = ws.index.get(op.id);
      if (!at || ws.pages[at.p].id !== op.pageId) throw new OpConflict(`Element ${op.id} no longer exists on that page.`, { nodeId: op.id });
      const page = ws.page(op.pageId, true);
      const node = page.elements[at.e];
      if (!meets(op.expect, node)) throw new OpConflict(`Element ${op.id} changed before removal.`, { nodeId: op.id });
      page.elements.splice(at.e, 1);
      ws.reindex();
      return { op: "insertNode", pageId: op.pageId, index: at.e, node: clone(node) };
    }
    case "orderNodes": {
      const page = ws.page(op.pageId, true);
      if (!page) throw new OpConflict(`Page ${op.pageId} no longer exists.`);
      const current = page.elements.map((n) => n.id);
      if (!meets(op.expect, current)) throw new OpConflict(`Layer order on ${op.pageId} changed.`);
      if (!Array.isArray(op.ids) || op.ids.length !== current.length || new Set(op.ids).size !== current.length || op.ids.some((id) => !current.includes(id)))
        throw new OpConflict("Layer order must list every element on the page exactly once.");
      const byId = new Map(page.elements.map((n) => [n.id, n]));
      page.elements.splice(0, page.elements.length, ...op.ids.map((id) => byId.get(id)));
      ws.reindex();
      return { op: "orderNodes", pageId: op.pageId, ids: current, expect: { value: [...op.ids] } };
    }
    case "insertPage": {
      if (!isObject(op.page) || typeof op.page.id !== "string" || !Array.isArray(op.page.elements)) throw new OpConflict("insertPage needs a page with elements.");
      if (ws.pageIndex.has(op.page.id)) throw new OpConflict(`Page ${op.page.id} already exists.`);
      if (op.page.elements.some((n) => ws.index.has(n.id))) throw new OpConflict("Inserted page reuses existing element ids.");
      const index = Number.isInteger(op.index) ? Math.max(0, Math.min(op.index, ws.pages.length)) : ws.pages.length;
      ws.pages.splice(index, 0, clone(op.page));
      ws.pagesCopied.add(op.page.id);
      ws.reindex();
      return { op: "removePage", id: op.page.id, expect: { value: clone(op.page) } };
    }
    case "removePage": {
      const p = ws.pageIndex.get(op.id);
      if (p === undefined) throw new OpConflict(`Page ${op.id} no longer exists.`);
      if (ws.pages.length === 1) throw new OpConflict("A design needs at least one page.");
      const page = ws.pages[p];
      if (!meets(op.expect, page)) throw new OpConflict(`Page ${op.id} changed before removal.`);
      ws.pages.splice(p, 1);
      ws.reindex();
      return { op: "insertPage", index: p, page: clone(page) };
    }
    case "orderPages": {
      const current = ws.pages.map((p) => p.id);
      if (!meets(op.expect, current)) throw new OpConflict("Page order changed.");
      if (!Array.isArray(op.ids) || op.ids.length !== current.length || new Set(op.ids).size !== current.length || op.ids.some((id) => !current.includes(id)))
        throw new OpConflict("Page order must list every page exactly once.");
      const byId = new Map(ws.pages.map((p) => [p.id, p]));
      ws.pages = op.ids.map((id) => byId.get(id));
      ws.reindex();
      return { op: "orderPages", ids: current, expect: { value: [...op.ids] } };
    }
    default:
      throw new OpConflict(`Unknown operation ${op && op.op}.`);
  }
}

// Applies a batch atomically. Returns the new document (sharing unchanged
// structure with the input), the inverse batch, and which pages/nodes changed.
function applyOps(doc, ops) {
  if (!Array.isArray(ops)) throw new OpConflict("Operations must be an array.");
  const ws = workspace(doc);
  const inverse = [];
  const pages = new Set(), nodes = new Set();
  for (const op of ops) {
    const pageOf = (id) => { const at = ws.index.get(id); return at ? ws.pages[at.p].id : null; };
    if (op && op.op === "set" && op.path && op.path[0] === "node") { nodes.add(op.path[1]); const p = pageOf(op.path[1]); if (p) pages.add(p); }
    if (op && op.op === "set" && op.path && op.path[0] === "page") pages.add(op.path[1]);
    if (op && op.op === "removeNode") { nodes.add(op.id); pages.add(op.pageId); }
    if (op && ["insertNode", "orderNodes"].includes(op.op)) { pages.add(op.pageId); if (op.node) nodes.add(op.node.id); }
    if (op && ["insertPage", "removePage"].includes(op.op)) pages.add(op.page ? op.page.id : op.id);
    if (op && op.op === "set" && op.path && op.path[0] === "doc") pages.add("*");
    inverse.push(applyOne(ws, op));
  }
  return { doc: ws.finish(), inverse: inverse.reverse(), touched: { pages: [...pages], nodes: [...nodes] } };
}

// Structural diff that emits the smallest ID-addressed batch turning `a` into
// `b`. Reference-equal branches are skipped without inspection, so diffing two
// immutable revisions of a large document costs roughly the size of the edit.
function diffDocs(a, b) {
  const ops = [];
  if (a === b) return ops;
  for (const key of new Set([...Object.keys(a), ...Object.keys(b)])) {
    if (key === "pages" || a[key] === b[key]) continue;
    if (KEYED_DOC_KEYS.has(key) && isObject(a[key]) && isObject(b[key])) {
      for (const sub of new Set([...Object.keys(a[key]), ...Object.keys(b[key])])) {
        if (a[key][sub] === b[key][sub] || deepEqual(a[key][sub], b[key][sub])) continue;
        ops.push(setOp(["doc", key, sub], a[key][sub], b[key][sub]));
      }
    } else if (!deepEqual(a[key], b[key])) ops.push(setOp(["doc", key], a[key], b[key]));
  }
  if (a.pages === b.pages) return ops;
  const aPages = new Map(a.pages.map((p) => [p.id, p]));
  const bPages = new Map(b.pages.map((p) => [p.id, p]));
  const aNodes = new Map(), bNodes = new Map();
  a.pages.forEach((p) => p.elements.forEach((n) => aNodes.set(n.id, { page: p.id, node: n })));
  b.pages.forEach((p) => p.elements.forEach((n) => bNodes.set(n.id, { page: p.id, node: n })));
  const removals = [], inserts = [], sets = [];
  // Nodes that left their page (deleted or moved elsewhere).
  for (const [id, at] of aNodes) {
    const next = bNodes.get(id);
    if (!next || next.page !== at.page) {
      if (bPages.has(at.page)) removals.push({ op: "removeNode", pageId: at.page, id, expect: { value: clone(at.node) } });
    }
  }
  const pageRemovals = [];
  for (const [id, page] of aPages) if (!bPages.has(id)) pageRemovals.push({ op: "removePage", id, expect: { value: clone(page) } });
  const pageInserts = [];
  b.pages.forEach((page, index) => {
    if (!aPages.has(page.id)) pageInserts.push({ op: "insertPage", index, page: clone(page) });
  });
  for (const page of b.pages) {
    const before = aPages.get(page.id);
    if (!before) continue;
    if (before !== page) {
      for (const key of new Set([...Object.keys(before), ...Object.keys(page)])) {
        if (key === "id" || PAGE_KEYS_SKIPPED.has(key) || before[key] === page[key] || deepEqual(before[key], page[key])) continue;
        sets.push(setOp(["page", page.id, key], before[key], page[key]));
      }
    }
    if (before.elements === page.elements) continue;
    page.elements.forEach((node, index) => {
      const prior = aNodes.get(node.id);
      if (!prior || prior.page !== page.id) {
        inserts.push({ op: "insertNode", pageId: page.id, index, node: clone(node) });
        return;
      }
      if (prior.node === node) return;
      for (const key of new Set([...Object.keys(prior.node), ...Object.keys(node)])) {
        if (key === "id" || prior.node[key] === node[key] || deepEqual(prior.node[key], node[key])) continue;
        sets.push(setOp(["node", node.id, key], prior.node[key], node[key]));
      }
    });
  }
  ops.push(...pageRemovals, ...removals, ...pageInserts, ...inserts);
  // Final orders are compared after removals/insertions have been applied.
  const pageOrder = b.pages.map((p) => p.id);
  const afterPageOps = [...a.pages.map((p) => p.id).filter((id) => bPages.has(id))];
  pageInserts.forEach((op) => afterPageOps.splice(Math.min(op.index, afterPageOps.length), 0, op.page.id));
  if (afterPageOps.join("\u0000") !== pageOrder.join("\u0000")) ops.push({ op: "orderPages", ids: pageOrder, expect: { value: afterPageOps } });
  for (const page of b.pages) {
    const before = aPages.get(page.id);
    if (!before || before.elements === page.elements) continue;
    const order = before.elements.map((n) => n.id).filter((id) => { const next = bNodes.get(id); return next && next.page === page.id; });
    inserts.filter((op) => op.pageId === page.id).forEach((op) => order.splice(Math.min(op.index, order.length), 0, op.node.id));
    const target = page.elements.map((n) => n.id);
    if (order.join("\u0000") !== target.join("\u0000")) ops.push({ op: "orderNodes", pageId: page.id, ids: target, expect: { value: order } });
  }
  ops.push(...sets);
  return ops;
}

function setOp(path, before, after) {
  return after === undefined
    ? { op: "set", path, unset: true, expect: expectOf(before) }
    : { op: "set", path, value: clone(after), expect: expectOf(before) };
}

// Pages whose printed result a batch can affect ("*" means every page).
function affectedPages(doc, ops) {
  const pages = new Set();
  const nodePage = new Map();
  doc.pages.forEach((p) => p.elements.forEach((n) => nodePage.set(n.id, p.id)));
  for (const op of ops) {
    if (op.op === "set" && op.path[0] === "doc") return doc.pages.map((p) => p.id);
    if (op.op === "set" && op.path[0] === "page") pages.add(op.path[1]);
    if (op.op === "set" && op.path[0] === "node" && nodePage.has(op.path[1])) pages.add(nodePage.get(op.path[1]));
    if (op.pageId) pages.add(op.pageId);
    if (op.op === "insertPage") pages.add(op.page.id);
    if (op.op === "removePage" || op.op === "orderPages") (op.ids || [op.id]).forEach((id) => pages.add(id));
  }
  return doc.pages.map((p) => p.id).filter((id) => pages.has(id));
}

function opsSize(ops) {
  return JSON.stringify(ops).length;
}

module.exports = { applyOps, diffDocs, affectedPages, deepEqual, clone, OpConflict, opsSize, expectOf, getIn };
